'use client';

import { AnimatePresence, motion } from 'motion/react';
import { useCallback, useState } from 'react';

import { CloseIcon } from '@/components/primitives/icons/CloseIcon';
import { MagnifierIcon } from '@/components/primitives/icons/MagnifierIcon';
import { ThreatIcon } from '@/components/primitives/icons/ThreatIcon';
import { Media } from '@/components/primitives/Media';
import { GA_LINK_PLACEMENT } from '@/constants/analytics';
import {
  PRESSURE_CARD_HOVER_MEDIA,
  PRESSURE_CARD_HOVER_ROTATE_DEG,
  PRESSURE_CARD_HOVER_Y_PX,
} from '@/constants/pressureCards';
import { useMatchMedia } from '@/hooks/useMatchMedia';
import { usePrefersReducedMotion } from '@/hooks/usePrefersReducedMotion';
import { trackOutboundClick } from '@/lib/analytics/trackEvent';
import { durations, easeStandard } from '@/motion/tokens';

import styles from './PressureCard.module.css';
import { usePressureCardScrollCue } from './usePressureCardScrollCue';

import type { PressureCardEntry } from '@/content/data/pressure-cards';

export type ResolvedPressureCard = Omit<PressureCardEntry, 'count'> & {
  count: string;
  label: string;
  body: string;
  countLabel: string;
  expandLabel: string;
  collapseLabel: string;
  readArticleLabel: string;
};

type PressureCardProps = {
  card: ResolvedPressureCard;
  selected: boolean;
  onToggle: (id: string) => void;
};

function stopRevealInteractionPropagation(event: React.SyntheticEvent) {
  event.stopPropagation();
}

export function PressureCard({ card, selected, onToggle }: PressureCardProps) {
  const reducedMotion = usePrefersReducedMotion();
  const hoverEnabled = useMatchMedia(PRESSURE_CARD_HOVER_MEDIA);
  const [hovered, setHovered] = useState(false);
  const { viewportRef, showScrollCue, updateScrollCue } =
    usePressureCardScrollCue(selected);

  const handleClick = useCallback(() => {
    onToggle(card.id);
  }, [card.id, onToggle]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.target !== event.currentTarget) return;

      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onToggle(card.id);
      }
    },
    [card.id, onToggle],
  );

  const handleArticleClick = useCallback((event: React.MouseEvent<HTMLAnchorElement>) => {
    event.stopPropagation();
    trackOutboundClick({
      href: card.articleHref,
      linkPlacement: GA_LINK_PLACEMENT.pressureArticle,
    });
  }, [card.articleHref]);

  const hoverActive = hoverEnabled && hovered;
  const hoverMotionActive = hoverActive && !reducedMotion;

  return (
    <motion.div
      role="button"
      tabIndex={0}
      className={styles.root}
      aria-pressed={selected}
      aria-label={`${card.label}. ${card.count} ${card.countLabel}. ${selected ? card.collapseLabel : card.expandLabel}`}
      data-selected={selected || undefined}
      data-hovered={hoverActive || undefined}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      onMouseEnter={hoverEnabled ? () => setHovered(true) : undefined}
      onMouseLeave={hoverEnabled ? () => setHovered(false) : undefined}
      onFocus={hoverEnabled ? () => setHovered(true) : undefined}
      onBlur={hoverEnabled ? () => setHovered(false) : undefined}
      animate={
        reducedMotion
          ? undefined
          : {
              // Figma Card hover: clockwise tilt + slight lift — no blur / shadow.
              rotate: hoverMotionActive ? PRESSURE_CARD_HOVER_ROTATE_DEG : 0,
              y: hoverMotionActive ? PRESSURE_CARD_HOVER_Y_PX : 0,
            }
      }
      transition={{ duration: durations.fast, ease: easeStandard }}
    >
      <span className={styles.bg} aria-hidden>
        <Media
          sources={card.imageSrc}
          sizes="(min-width: 80rem) 20vw, (min-width: 48rem) 33vw, 72vw"
          alt=""
          width={980}
          height={1576}
          className={styles.bgImage}
          loading="lazy"
        />
      </span>
      <span className={styles.scrim} aria-hidden />

      <span className={styles.chromeIcon} aria-hidden>
        {selected ? <CloseIcon size={24} /> : <MagnifierIcon />}
      </span>

      <AnimatePresence initial={false}>
        {selected ? (
          <motion.div
            key="reveal"
            className={styles.reveal}
            initial={reducedMotion ? false : { opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={reducedMotion ? undefined : { opacity: 0 }}
            transition={{ duration: durations.fast, ease: easeStandard }}
          >
            <div className={styles.revealScrollRegion}>
              <div
                ref={viewportRef}
                className={styles.revealBodyViewport}
                role="region"
                aria-label={card.label}
                tabIndex={0}
                data-lenis-prevent
                onClick={stopRevealInteractionPropagation}
                onKeyDown={stopRevealInteractionPropagation}
                onScroll={updateScrollCue}
              >
                <p className={styles.revealBody}>{card.body}</p>
              </div>
              <span
                className={styles.revealScrollCue}
                data-visible={showScrollCue || undefined}
                aria-hidden
              />
            </div>
            <a
              href={card.articleHref}
              className={styles.revealCta}
              target="_blank"
              rel="noopener noreferrer"
              onClick={handleArticleClick}
            >
              {card.readArticleLabel}
            </a>
          </motion.div>
        ) : (
          <motion.span
            key="face"
            className={styles.face}
            initial={reducedMotion ? false : { opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={reducedMotion ? undefined : { opacity: 0 }}
            transition={{ duration: durations.fast, ease: easeStandard }}
          >
            <span className={styles.name}>
              <ThreatIcon id={card.icon} className={styles.icon} />
              <span className={styles.label}>{card.label}</span>
            </span>
            <span className={styles.countBlock}>
              <span className={styles.count}>{card.count}</span>
              <span className={styles.countLabel}>{card.countLabel}</span>
            </span>
          </motion.span>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
